import React, { useState, useEffect, useCallback, useRef } from "react";
import DOMReplayViewer from "../DomReplayViewer";
import MediaSlider from "./MediaSlider";
import { motion, AnimatePresence } from "motion/react";
import Sidebar from "./Sidebar";
import { type Report } from "@/lib/types";
import { useWindowSize } from "usehooks-ts";

interface MediaViewerProps {
  mediaUrl: string[];
  mediaType: "image" | "video" | "dom-replay" | "comments";
  filename: string;
  uploadDate: string;
  fileSize: number;
  // onDelete: () => void;
  onEdit: () => void;
  onShare: (url: string) => void;
  report?: Report & { elementComments?: ElementComment[] };
}

interface ElementComment {
  url: string;
  selector: string;
  xpath?: string;
  offsetX: number;
  offsetY: number;
  fallbackX?: number;
  fallbackY?: number;
  text: string;
  commentNumber: number;
  createdAt: Date;
  updatedAt: Date;
  textContent?: string;
  directText?: string;
  attributes?: Record<string, string>;
  styles?: {
    color?: string;
    backgroundColor?: string;
    fontSize?: string;
    fontFamily?: string;
  };
  innerHTML?: string;
  outerHTML?: string;
  elementWidth?: number;
  elementHeight?: number;
  screenshot?: string;
  screenshotX?: number;
  screenshotY?: number;
  screenshotXPercent?: number;
  screenshotYPercent?: number;
  username?: string;
  images?: string[];
}

const SIDEBAR_INITIAL_WIDTH = 480;
const SIDEBAR_WIDTH_STORAGE_KEY = "sidebarDividerPosition";

// Calculate initial divider position based on desired sidebar width of 480px
const getInitialDividerPosition = () => {
  // First check localStorage for saved position
  if (typeof window !== "undefined") {
    const savedPosition = localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
    if (savedPosition) {
      const parsed = parseFloat(savedPosition);
      if (!isNaN(parsed) && parsed >= 30 && parsed <= 85) {
        return parsed;
      }
    }
    // Fall back to calculating from initial width
    if (window.innerWidth > 1023) {
      const sidebarPercentage =
        (SIDEBAR_INITIAL_WIDTH / window.innerWidth) * 100;
      return Math.min(Math.max(100 - sidebarPercentage, 30), 85);
    }
  }
  return 65;
};

const MediaViewer: React.FC<MediaViewerProps> = ({
  mediaUrl,
  mediaType,
  filename,
  uploadDate,
  fileSize,
  // onDelete,
  onEdit,
  onShare,
  report,
}) => {
  const { width } = useWindowSize();

  const [dividerPosition, setDividerPosition] = useState(
    getInitialDividerPosition
  );
  const [isResizing, setIsResizing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [sidebarVerticalDividerPosition, setSidebarVerticalDividerPosition] =
    useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchParams = new URLSearchParams(window.location.search);
  const reportId = searchParams.get("id");

  // Handle mouse move during resize
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !containerRef.current) return;

      e.preventDefault();
      const containerRect = containerRef.current.getBoundingClientRect();
      const newPosition =
        ((e.clientX - containerRect.left) / containerRect.width) * 100;

      setDividerPosition(Math.min(Math.max(newPosition, 30), 85));
    },
    [isResizing]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = "auto";
    document.body.style.userSelect = "auto";
    // Save the current divider position to localStorage
    setDividerPosition((currentPosition) => {
      localStorage.setItem(
        SIDEBAR_WIDTH_STORAGE_KEY,
        currentPosition.toString()
      );
      return currentPosition;
    });
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "auto";
      document.body.style.userSelect = "auto";
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Process media URLs into slides
  const getMediaSlides = () => {
    // Check if it's DOM replay
    if (mediaUrl.includes("dom-replay") || mediaType === "dom-replay") {
      return null;
    }

    // For "comments" mediaType, create slides from elementComments screenshots
    // Group comments by screenshot URL so multiple comments on the same screenshot appear together
    if (mediaType === "comments" && report?.elementComments) {
      const commentsWithScreenshots = report.elementComments.filter(
        (comment: any) => comment.screenshot
      );

      // Group comments by screenshot URL
      const screenshotGroups = new Map<string, ElementComment[]>();
      commentsWithScreenshots.forEach((comment: ElementComment) => {
        const url = comment.screenshot!;
        if (!screenshotGroups.has(url)) {
          screenshotGroups.set(url, []);
        }
        screenshotGroups.get(url)!.push(comment);
      });

      // Create slides from grouped screenshots
      return Array.from(screenshotGroups.entries()).map(
        ([url, comments], index) => ({
          mediaType: "image",
          url,
          filename: `screenshot-${index + 1}`,
          // Include all comments for this screenshot
          elementComments: comments,
        })
      );
    }

    // Map URLs to slides
    return mediaUrl.map((url: string, index: number) => {
      const extension = url.split(".").pop()?.toLowerCase();
      const type = ["mp4", "webm", "mov", "avi"].includes(extension || "")
        ? "video"
        : "image";

      return {
        mediaType: type,
        url: url,
        filename: url.split("/").pop() || `media-${index + 1}.${extension}`,
      };
    });
  };

  const mediaSlides = getMediaSlides();
  const isDomReplay =
    mediaUrl.includes("dom-replay") || mediaType === "dom-replay";

  // useEffect(() => {
  //   console.log({ report });
  // }, [report]);

  return (
    <div
      ref={containerRef}
      className="flex flex-col lg:flex-row lg:h-[calc(100%-48px)] relative"
    >
      <div
        className="flex flex-col items-center justify-center"
        style={{
          width: report && width > 1023 ? `${dividerPosition}%` : "100%",
          height: width < 1024 ? "100vh" : "100%",
          minWidth: 0,
          flex: report && width > 1023 ? "1 1 auto" : undefined,
        }}
      >
        <div className="w-full mx-auto lg:h-full">
          <div className="rounded-lg shadow-lg lg:overflow-hidden h-full">
            <div className="h-full">
              {isDomReplay ? (
                <div className="flex w-full h-full items-center justify-center px-0 lg:px-[56px]">
                  <DOMReplayViewer
                    reportId={reportId}
                    containerWidth={dividerPosition}
                  />
                </div>
              ) : (
                <MediaSlider
                  mediaType={mediaType}
                  slides={mediaSlides || []}
                  report={report}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {report && (
        <>
          <div
            className="w-[8px] cursor-col-resize hidden lg:flex items-center justify-center relative group"
            onMouseDown={() => setIsResizing(true)}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            style={{
              cursor: isResizing
                ? "col-resize"
                : isHovering
                ? "col-resize"
                : "default",
            }}
          >
            <AnimatePresence>
              {(isHovering || isResizing) && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute left-0 right-0 flex items-center justify-center"
                  style={{
                    top: `${sidebarVerticalDividerPosition}%`,
                    transform: "translateY(-50%)",
                  }}
                >
                  <div
                    className={`h-16 w-[2px] bg-gray-900 rounded-full transition-all duration-200`}
                  ></div>
                </motion.div>
              )}
            </AnimatePresence>
            <div
              className="absolute inset-0 z-10"
              style={{ cursor: "col-resize" }}
            ></div>
          </div>

          <div
            style={{
              width: width > 1023 ? `${100 - dividerPosition}%` : "100%",
              minWidth: "350px",
              flexShrink: 0,
            }}
          >
            <Sidebar
              report={report}
              onDividerPositionChange={setSidebarVerticalDividerPosition}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default MediaViewer;
